import * as Api from '@ViewModels';
import * as React from 'react';
import { useInfiniteEventRegistrationSurveys } from '../../../../queries';

/** Fetch a generous page so events usually arrive in one request. */
const EVENTS_PAGE_SIZE = 100;

/**
 * The event-registration endpoint has no date filter, so we page through the account's
 * events. This caps the drain so an account with a large event history can't spin through
 * unbounded requests; at {@link EVENTS_PAGE_SIZE} per page this covers the common case and
 * bounds the worst one.
 */
const MAX_AUTO_PAGES = 5;

export interface IUseCalendarEventsResult {
	events: Api.IEventRegistrationSurvey[];
	isLoading: boolean;
}

/**
 * Fetch the organization's hosted events (event-registration surveys) for the calendar.
 * The endpoint has no date-range filter, so this returns the full account-scoped list
 * (callers filter to the visible window); past-registration events are included so the
 * calendar can still show events whose registration has closed but whose date is ahead.
 * Gated by `enabled` — pass the Surveys feature flag.
 */
export function useCalendarEvents({
	impersonationContext,
	enabled = true,
}: {
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}): IUseCalendarEventsResult {
	const query = useInfiniteEventRegistrationSurveys({
		shouldIncludeArchived: false,
		shouldIncludeExpired: true,
		pageSize: EVENTS_PAGE_SIZE,
		enabled,
		impersonationContext,
		keepPreviousData: true,
	});

	const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
	const pagesLoaded = query.data?.pages.length ?? 0;

	// Eagerly drain remaining pages so the calendar sees every event, capped so a large
	// event history can't spin through unbounded requests.
	React.useEffect(() => {
		if (enabled && hasNextPage && !isFetchingNextPage && pagesLoaded < MAX_AUTO_PAGES) {
			fetchNextPage();
		}
	}, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage, pagesLoaded]);

	const events = React.useMemo(() => query.data?.pages.flatMap(page => page.values) ?? [], [query.data?.pages]);

	return {
		events,
		isLoading: enabled ? query.isPending : false,
	};
}
